/**
 * The influence map's groups (ruling `...-81-2-10`): one dimension's active
 * values, each with its account count and the chosen metric's total, and a
 * chevron into Contas monitoradas filtered by that group's tag. The chip
 * privileging a group's account kind was dropped by owner ruling: the route
 * computes no kind at all, and this card draws none.
 *
 * `breakdown` is read by the container (`Overview.tsx`), same as `reading`
 * is for every other card here: `OverviewView`'s tree stays pure-render, so
 * a caller can still exercise it with a plain object and no `QueryClient`.
 * Only `BreakdownSelect`'s own vocabulary lookup is a self-contained
 * exception, being the one piece every one of the three screens mounts
 * verbatim.
 */

import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';

import { getLocale } from '../../../i18n';
import { count } from '../../../chrome/measure/format';
import { Absent } from '../../../chrome/measure';
import { IconHint } from '../../../chrome/hint';
import { RankRow } from '../../../chrome/chart';
import { EmptyState, QueryStates } from '../../../chrome/state';
import type { ApiFailureError } from '../../../lib/http/errors';
import { accountsPath } from '../../control/paths';
import type { Breakdown, BreakdownMetric } from '../api/schemas';
import { ov, ovPlural } from './messages';
import { OverviewCard } from './OverviewCard';
import { SeriesToggle } from './SeriesToggle';
import { BreakdownSelect } from '../shared/BreakdownSelect';
import { ds } from '../shared/messages';

/** `?tags=` is the accounts list's own filter param (`Accounts.tsx`), a JSON array as the
 *  router's own search serialiser writes one. */
function accountsFilteredByTag(campaignId: string, tag: string): string {
  return `${accountsPath(campaignId)}?${new URLSearchParams({ tags: JSON.stringify([tag]) }).toString()}`;
}

function ChevronLink(props: { href: string; label: string }): ReactNode {
  return (
    <IconHint label={props.label}>
      <a href={props.href} className="text-muted-foreground hover:text-foreground shrink-0">
        <ChevronRight aria-hidden className="size-4" />
      </a>
    </IconHint>
  );
}

export function InfluenceMap(props: {
  campaignId: string;
  breakdown: UseQueryResult<Breakdown, ApiFailureError>;
  dimensionId: string | null;
  onDimensionChange: (dimensionId: string) => void;
  metric: BreakdownMetric;
  onMetricChange: (metric: BreakdownMetric) => void;
}): ReactNode {
  const { breakdown } = props;

  return (
    <OverviewCard
      title={ov('groups.title')}
      aside={
        <div className="flex flex-wrap items-center gap-2">
          <BreakdownSelect
            campaignId={props.campaignId}
            value={props.dimensionId}
            onChange={props.onDimensionChange}
          />
          <SeriesToggle metric={props.metric} onMetricChange={props.onMetricChange} />
        </div>
      }
    >
      <QueryStates
        query={breakdown}
        isEmpty={(data) => data.groups.length === 0}
        empty={<EmptyState title={ds('groups.empty')} />}
      >
        {(data) =>
          data.groups.map((group) => (
            <div key={group.value_id} className="flex items-center gap-2">
              <div className="flex-1">
                <RankRow
                  label={group.name}
                  value={
                    group.total === null ? (
                      <Absent reason="not_measured" />
                    ) : (
                      count(group.total, getLocale())
                    )
                  }
                />
                <p data-numeric className="text-caption text-muted-foreground">
                  {ovPlural('groups.accounts.other', group.account_count, {
                    n: count(group.account_count, getLocale()),
                  })}
                </p>
              </div>
              <ChevronLink
                href={accountsFilteredByTag(props.campaignId, group.name)}
                label={ov('groups.open', { group: group.name })}
              />
            </div>
          ))
        }
      </QueryStates>
    </OverviewCard>
  );
}
